// Batched voxel building renderer — one InstancedMesh per prefab-kind
// (P0.1-full day 2 afternoon).
//
// Mirrors VoxelUnitBatch for structures. Buildings are static (no billboard,
// no bob), so the per-frame useFrame only updates the instance matrix when
// entries change — which for a static building is "never after initial
// placement". We still write matrices in a useEffect to stay correct.
//
// Construction-progress buildings (wireframe scaffolding) are intentionally
// NOT batched — they're rare and transient. Cities (the only current
// consumer) always pass constructionProgress = undefined, so this is fine
// for now. If a future caller needs scaffolding, keep those on the legacy
// per-building VoxelBuilding component (Option A in the design doc).
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.
import { playerColor } from '@aod/ui';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { getBuildingGeometry } from './voxelGeometryCache.js';
import { getVoxelMaterial } from './voxelMaterial.js';

// Module-level dummy — reused across matrix writes.
const _dummy = new THREE.Object3D();
const _factionColor = new THREE.Color();

export interface BatchedBuilding {
  readonly id: string;
  readonly ownerId?: number;
  readonly baseX: number;
  readonly baseZ: number;
  readonly baseY: number;
  readonly selected: boolean;
}

interface VoxelBuildingBatchProps {
  readonly prefabId: string;
  readonly buildings: ReadonlyArray<BatchedBuilding>;
  readonly onClick?: (buildingId: string) => void;
}

export function VoxelBuildingBatch({ prefabId, buildings, onClick }: VoxelBuildingBatchProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const baseGeometry = getBuildingGeometry(prefabId);
  const geometry = useMemo(() => baseGeometry?.clone(), [baseGeometry]);
  const material = useMemo(() => getVoxelMaterial(), []);

  const factionArr = useMemo(() => new Float32Array(buildings.length * 3), [buildings.length]);
  const selArr = useMemo(() => new Float32Array(buildings.length), [buildings.length]);
  const flashArr = useMemo(() => new Float32Array(buildings.length), [buildings.length]);

  useEffect(() => {
    if (!geometry) return;
    geometry.setAttribute('aFactionColor', new THREE.InstancedBufferAttribute(factionArr, 3));
    geometry.setAttribute('aSelected', new THREE.InstancedBufferAttribute(selArr, 1));
    geometry.setAttribute('aFlash', new THREE.InstancedBufferAttribute(flashArr, 1));
  }, [geometry, factionArr, selArr, flashArr]);

  // Per-state-change: write transforms + faction/selection/flash values.
  // Buildings are static so this re-runs only when the entries array changes.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || !geometry) return;

    for (let i = 0; i < buildings.length; i++) {
      const b = buildings[i];
      if (!b) continue;
      _dummy.position.set(b.baseX, b.baseY, b.baseZ);
      _dummy.rotation.set(0, 0, 0);
      _dummy.scale.setScalar(1);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);

      _factionColor.set(playerColor(b.ownerId));
      factionArr[i * 3] = _factionColor.r;
      factionArr[i * 3 + 1] = _factionColor.g;
      factionArr[i * 3 + 2] = _factionColor.b;
      selArr[i] = b.selected ? 1 : 0;
      flashArr[i] = 0;
    }
    const fa = geometry.attributes.aFactionColor as THREE.BufferAttribute | undefined;
    const sa = geometry.attributes.aSelected as THREE.BufferAttribute | undefined;
    const fla = geometry.attributes.aFlash as THREE.BufferAttribute | undefined;
    if (fa) fa.needsUpdate = true;
    if (sa) sa.needsUpdate = true;
    if (fla) fla.needsUpdate = true;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.count = buildings.length;
  }, [buildings, geometry, factionArr, selArr, flashArr]);

  if (!geometry) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, buildings.length]}
      onClick={(e) => {
        e.stopPropagation();
        const idx = e.instanceId;
        if (idx === undefined) return;
        const b = buildings[idx];
        if (b) onClick?.(b.id);
      }}
      renderOrder={RenderLayer.Buildings}
      frustumCulled={false}
    />
  );
}
